/**
 * Avatar utilities for the Clients list page.
 *
 * Initials rules from: "Acceptance Criteria — Company Initials Avatar" (agreed PDF spec).
 * Color hash approach from: containers/modal/ai-project-planning/constants.ts (existing codebase pattern).
 *
 * Stop words (PDF §3):        the, and, of, for, &
 * Company suffixes (PDF §2):  Ltd, Limited, Inc, Incorporated, LLC, Pty, Pty Ltd, Co, Company, Corp, Corporation
 * Symbols (PDF §8):           & + - /  → treated as word separators
 * Numbers (PDF §7):           allowed as initial only if the name starts with a digit
 * Boundaries (PDF §8):        digit↔letter transitions split a token ("3D" → "3","D")
 */

// Stop words — exactly as defined in the PDF. Do NOT expand this list without updating the spec.
const STOP_WORDS = new Set(['the', 'and', 'of', 'for', '&']);

// Company suffixes — exactly as defined in the PDF. Case-insensitive comparison is applied below.
const COMPANY_SUFFIXES = new Set([
	'ltd',
	'limited',
	'inc',
	'incorporated',
	'llc',
	'pty',
	'co',
	'company',
	'corp',
	'corporation',
]);

/**
 * Splits a company name into tokens by:
 *   1. Replacing symbols (&, +, -, /) with spaces
 *   2. Inserting a space at digit↔letter boundaries  ("3D" → "3 D", "A1" → "A 1")
 *   3. Splitting on whitespace and discarding empty tokens
 */
function tokenise(name: string): string[] {
	return name
		.trim()
		.replace(/[&+\-/]/g, ' ') // symbols → space
		.replace(/(\d)([a-zA-Z])/g, '$1 $2') // digit→letter boundary
		.replace(/([a-zA-Z])(\d)/g, '$1 $2') // letter→digit boundary
		.split(/\s+/)
		.filter(t => t.length > 0);
}

function isMeaningful(token: string, allowDigitTokens: boolean): boolean {
	const lower = token.toLowerCase();
	if (STOP_WORDS.has(lower)) return false;
	if (COMPANY_SUFFIXES.has(lower)) return false;
	// Standalone all-digit tokens are only allowed when the name itself starts with a digit (PDF §7)
	if (/^\d+$/.test(token) && !allowDigitTokens) return false;
	return true;
}

/**
 * Returns 1–2 uppercase initials for a company name, per the agreed avatar spec.
 * Returns '?' for empty input or when no meaningful words remain.
 */
export function getClientInitials(name: string): string {
	if (!name.trim()) return '?';

	const allowDigitTokens = /^\d/.test(name.trim());
	const meaningful = tokenise(name).filter(t => isMeaningful(t, allowDigitTokens));

	if (meaningful.length === 0) return '?';
	if (meaningful.length === 1) return meaningful[0][0].toUpperCase();
	return (meaningful[0][0] + meaningful[1][0]).toUpperCase();
}
